// Standorte (Hauptstandort / Niederlassung / Partnerbetrieb) — eine Quelle für
// Footer, Kontaktseite, Online-Buchung, Termine und Check-in.
//
// Daten kommen aus `GET /api/locations/public` (nur veröffentlichte Standorte),
// über `cachedGet` dedupliziert. Pflege: Master-Panel → Tab "Standorte"; nach
// jeder Änderung `invalidateLocations()` aufrufen, damit Besucher sofort
// die neue Fassung sehen.
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { cachedGet, invalidateApiCache } from './apiCache';

const API_URL = process.env.REACT_APP_BACKEND_URL;
export const LOCATIONS_URL = `${API_URL}/api/locations/public`;

export const invalidateLocations = () => invalidateApiCache(LOCATIONS_URL);

export const LOCATION_KIND_LABELS = {
  main: 'Hauptstandort',
  branch: 'Niederlassung',
  partner: 'Partnerbetrieb',
};

export const DAY_FIELDS = [
  { key: 'monday', label: 'Montag', short: 'Mo' },
  { key: 'tuesday', label: 'Dienstag', short: 'Di' },
  { key: 'wednesday', label: 'Mittwoch', short: 'Mi' },
  { key: 'thursday', label: 'Donnerstag', short: 'Do' },
  { key: 'friday', label: 'Freitag', short: 'Fr' },
  { key: 'saturday', label: 'Samstag', short: 'Sa' },
  { key: 'sunday', label: 'Sonntag', short: 'So' },
];

/** Öffentliche Standorte; `null` solange geladen wird, danach Liste (bei Fehler leer). */
export const useLocations = () => {
  const [locations, setLocations] = useState(null);
  useEffect(() => {
    let alive = true;
    cachedGet(LOCATIONS_URL)
      .then((d) => { if (alive) setLocations(Array.isArray(d) ? d : []); })
      .catch(() => { if (alive) setLocations([]); });
    return () => { alive = false; };
  }, []);
  return locations;
};

/** Fasst aufeinanderfolgende Tage mit gleichen Zeiten zusammen: Mo–Do 08:00 - 18:00. */
export const compactHours = (hours) => {
  if (!hours || !Object.keys(hours).length) return [];
  const groups = [];
  DAY_FIELDS.forEach((d) => {
    const val = hours[d.key] || 'Geschlossen';
    const last = groups[groups.length - 1];
    if (last && last.hours === val) last.end = d;
    else groups.push({ start: d, end: d, hours: val });
  });
  return groups.map((g) => ({
    label: g.start.key === g.end.key ? g.start.short : `${g.start.short}–${g.end.short}`,
    hours: g.hours,
  }));
};

/** Einzeiler "Name — Straße, PLZ Ort" für Listen, Selects, Chips. */
export const locationLine = (loc) => {
  if (!loc) return '';
  const addr = loc.full_address || [loc.street, [loc.zip, loc.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [loc.name, addr].filter(Boolean).join(' — ');
};

/** Termine können einen Standort tragen (`location`) — Kurzform für Chips/Tabellen. */
export const locationShort = (loc) => {
  if (!loc) return '';
  return [loc.name, loc.city].filter(Boolean).join(' · ');
};

/**
 * Alle Standorte inkl. Entwürfe für Master/Admin (Zuweisen an Termine, Buchungen …).
 * Immer live (nicht gecacht), damit frisch angelegte Standorte sofort wählbar sind.
 */
export const useAdminLocations = (enabled = true) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/locations`);
      setLocations(Array.isArray(r.data) ? r.data : []);
    } catch {
      setLocations([]);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { if (enabled) reload(); }, [enabled, reload]);
  return { locations, loading, reload };
};
